import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axiosConfig';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import StravaActivityListCard from './StravaActivityListCard';
import ActivityMap from './ActivityMap';
import { Loader2, Search, ExternalLink, Scale } from 'lucide-react';

const BROWSE_SORT_OPTIONS = [
  { value: 'date_desc', labelKey: 'activities.sortDateDesc' },
  { value: 'date_asc', labelKey: 'activities.sortDateAsc' },
  { value: 'distance_desc', labelKey: 'activities.sortDistDesc' },
  { value: 'distance_asc', labelKey: 'activities.sortDistAsc' },
  { value: 'duration_desc', labelKey: 'activities.sortDurDesc' },
  { value: 'duration_asc', labelKey: 'activities.sortDurAsc' },
];

/**
 * Self-contained state so typing in browse filters does not re-render ActivityDetail (maps/charts).
 */
export default function ActivityFindSimilarModal({
  open,
  onOpenChange,
  currentActivityId,
  prefillDistanceMeters,
  t,
  onCompareSuggested,
  onCompareBrowse,
}) {
  const navigate = useNavigate();
  const prefillRef = useRef(prefillDistanceMeters);
  prefillRef.current = prefillDistanceMeters;

  const [similarTab, setSimilarTab] = useState('suggested');
  const [similarLoading, setSimilarLoading] = useState(false);
  const [similarActivities, setSimilarActivities] = useState([]);
  const [matchDimensions, setMatchDimensions] = useState([]);
  const [similarEmptyReason, setSimilarEmptyReason] = useState('none');
  const [similarFallbackUsed, setSimilarFallbackUsed] = useState(false);

  const [browseSearch, setBrowseSearch] = useState('');
  const [browseMinKm, setBrowseMinKm] = useState('');
  const [browseMaxKm, setBrowseMaxKm] = useState('');
  const [browseType, setBrowseType] = useState('');
  const [browseSort, setBrowseSort] = useState('date_desc');
  const [browseResults, setBrowseResults] = useState([]);
  const [browseTypes, setBrowseTypes] = useState([]);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [browseHasSearched, setBrowseHasSearched] = useState(false);

  useEffect(() => {
    if (!open || !currentActivityId) return undefined;

    const dm = prefillRef.current;
    const dkm = dm != null && Number.isFinite(Number(dm)) && Number(dm) > 0 ? Number(dm) / 1000 : 0;
    if (dkm > 0) {
      setBrowseMinKm((dkm * 0.8).toFixed(1));
      setBrowseMaxKm((dkm * 1.2).toFixed(1));
    } else {
      setBrowseMinKm('');
      setBrowseMaxKm('');
    }
    setSimilarTab('suggested');
    setBrowseHasSearched(false);
    setBrowseResults([]);
    setBrowseSearch('');
    setBrowseType('');
    setSimilarLoading(true);
    setSimilarActivities([]);
    setMatchDimensions([]);
    setSimilarEmptyReason('none');
    setSimilarFallbackUsed(false);

    let cancelled = false;
    api
      .get(
        `/api/strava/activity/${currentActivityId}/similar?dimensions=session_type,distance,elevation&limit=10`
      )
      .then((res) => {
        if (cancelled) return;
        setSimilarActivities(res.data.similar || []);
        setMatchDimensions(res.data.match_dimensions || []);
        setSimilarFallbackUsed(!!res.data.fallback_used);
        setSimilarEmptyReason(res.data.empty_reason || 'none');
      })
      .catch(() => {
        if (cancelled) return;
        setSimilarActivities([]);
        setSimilarEmptyReason('none');
      })
      .finally(() => {
        if (!cancelled) setSimilarLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, currentActivityId]);

  const handleBrowseActivitiesSearch = async () => {
    if (!currentActivityId) return;
    setBrowseLoading(true);
    setBrowseHasSearched(true);
    try {
      const params = new URLSearchParams({
        page: '1',
        limit: '30',
        sort: browseSort,
      });
      params.set('exclude_id', String(currentActivityId));
      if (browseSearch.trim()) params.set('search', browseSearch.trim());
      if (browseType.trim()) params.set('type', browseType.trim());
      const minM =
        browseMinKm.trim() !== '' ? Math.round(parseFloat(browseMinKm) * 1000) : null;
      const maxM =
        browseMaxKm.trim() !== '' ? Math.round(parseFloat(browseMaxKm) * 1000) : null;
      if (minM != null && Number.isFinite(minM) && minM > 0) {
        params.set('min_distance_m', String(minM));
      }
      if (maxM != null && Number.isFinite(maxM) && maxM > 0) {
        params.set('max_distance_m', String(maxM));
      }
      const res = await api.get(`/api/strava/activities?${params}`);
      setBrowseResults(res.data.activities || []);
      setBrowseTypes(res.data.types || []);
    } catch {
      setBrowseResults([]);
    } finally {
      setBrowseLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'flex max-h-[95vh] w-[min(95vw,52rem)] max-w-[min(95vw,52rem)] flex-col overflow-hidden gap-0 p-0 pt-6',
        )}
        priority='high'
      >
        <DialogHeader className='shrink-0 space-y-1.5 px-6 pr-14 text-left'>
          <DialogTitle>{t('activityDetail.findSimilar')}</DialogTitle>
          <DialogDescription className='text-xs text-muted-foreground'>
            {t('activityDetail.findSimilarDialogDesc')}
          </DialogDescription>
        </DialogHeader>
        <div className='citius-scrollbar min-h-0 flex-1 overflow-y-auto px-6 pb-6'>
          <Tabs value={similarTab} onValueChange={setSimilarTab} className='w-full'>
            <TabsList className='grid w-full grid-cols-2'>
              <TabsTrigger value='suggested'>{t('activityDetail.similarSuggestedTab')}</TabsTrigger>
              <TabsTrigger value='browse'>{t('activityDetail.similarBrowseTab')}</TabsTrigger>
            </TabsList>
            <TabsContent value='suggested' className='mt-3'>
              <div className='pr-1'>
                {similarLoading ? (
                  <div className='flex items-center justify-center py-8'>
                    <Loader2 className='h-8 w-8 animate-spin text-primary' />
                  </div>
                ) : similarActivities.length === 0 ? (
                  <p className='text-sm text-muted-foreground py-4'>
                    {similarEmptyReason === 'no_dna'
                      ? t('activityDetail.noSimilarNoDna')
                      : similarEmptyReason === 'no_match'
                        ? t('activityDetail.noSimilarNoMatch')
                        : t('activityDetail.noSimilarEmpty')}
                  </p>
                ) : (
                  <div className='space-y-3'>
                    {similarFallbackUsed && (
                      <p className='text-xs text-muted-foreground rounded-md border border-border bg-muted/40 px-2 py-1.5'>
                        {t('activityDetail.similarFallbackHint')}
                      </p>
                    )}
                    {similarActivities.map((a) => {
                      const suggestedHasMap = !!(a.summary_polyline || a.polyline);
                      return (
                      <div
                        key={a.id}
                        className='rounded-lg border border-border bg-card overflow-hidden hover:border-primary/30 transition-colors'
                      >
                        <div className='flex gap-3 p-3 items-start'>
                          <div
                            className={cn(
                              'min-w-0 shrink-0',
                              suggestedHasMap
                                ? 'max-w-[11rem] sm:max-w-[13rem]'
                                : 'flex-1',
                            )}
                          >
                            <p className='font-medium text-foreground truncate text-sm'>
                              {a.name || t('activityDetail.untitled')}
                            </p>
                            <div className='flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground mt-0.5'>
                              <span>{a.distance_km} km</span>
                              {a.pace_min_per_km && <span>{a.pace_min_per_km} /km</span>}
                              {a.session_type && <span>{a.session_type}</span>}
                              {a.region_name && <span>{a.region_name}</span>}
                            </div>
                            {matchDimensions.length > 0 && (
                              <div className='flex flex-wrap gap-1 mt-2'>
                                {matchDimensions.map((dim) => (
                                  <Badge
                                    key={dim}
                                    variant='secondary'
                                    className='text-[10px] px-1.5 py-0 font-normal'
                                  >
                                    {t(`activityDetail.matchDimension.${dim}`) || dim}
                                  </Badge>
                                ))}
                              </div>
                            )}
                            <p className='text-xs text-muted-foreground mt-1'>
                              {a.start_date_local
                                ? new Date(a.start_date_local).toLocaleDateString()
                                : ''}
                            </p>
                          </div>
                          {suggestedHasMap && (
                            <div
                              className='flex-1 min-w-[4.5rem] h-36 rounded-md overflow-hidden border border-border bg-muted/30'
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ActivityMap
                                encodedPolyline={a.polyline}
                                summaryPolyline={a.summary_polyline}
                                height={144}
                                className='!rounded-none !border-0 pointer-events-none'
                                fitPadding={[6, 6]}
                                fitBoundsMaxZoom={14}
                              />
                            </div>
                          )}
                          <div className='flex flex-col gap-1 shrink-0'>
                            <Button
                              variant='outline'
                              size='sm'
                              className='h-8 text-xs'
                              onClick={() => {
                                navigate(`/activity/${a.id}`);
                                onOpenChange(false);
                              }}
                            >
                              <ExternalLink className='h-3 w-3 mr-1' />
                              {t('activityDetail.openActivity')}
                            </Button>
                            <Button
                              variant='secondary'
                              size='sm'
                              className='h-8 text-xs'
                              onClick={() => {
                                onOpenChange(false);
                                onCompareSuggested(a, similarActivities);
                              }}
                            >
                              <Scale className='h-3 w-3 mr-1' />
                              {t('activityDetail.compare')}
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                    })}
                  </div>
                )}
              </div>
            </TabsContent>
            <TabsContent value='browse' className='mt-3 space-y-3'>
              <p className='text-xs text-muted-foreground'>{t('activityDetail.browseHint')}</p>
              <div className='grid gap-2 sm:grid-cols-2'>
                <div className='sm:col-span-2'>
                  <Label htmlFor='browse-search' className='text-xs'>
                    {t('activityDetail.browseSearchLabel')}
                  </Label>
                  <Input
                    id='browse-search'
                    value={browseSearch}
                    onChange={(e) => setBrowseSearch(e.target.value)}
                    placeholder={t('activityDetail.browseSearchPlaceholder')}
                    className='mt-1'
                  />
                </div>
                <div>
                  <Label htmlFor='browse-min-km' className='text-xs'>
                    {t('activityDetail.browseMinKm')}
                  </Label>
                  <Input
                    id='browse-min-km'
                    value={browseMinKm}
                    onChange={(e) => setBrowseMinKm(e.target.value)}
                    inputMode='decimal'
                    className='mt-1'
                  />
                </div>
                <div>
                  <Label htmlFor='browse-max-km' className='text-xs'>
                    {t('activityDetail.browseMaxKm')}
                  </Label>
                  <Input
                    id='browse-max-km'
                    value={browseMaxKm}
                    onChange={(e) => setBrowseMaxKm(e.target.value)}
                    inputMode='decimal'
                    className='mt-1'
                  />
                </div>
                <div>
                  <Label htmlFor='browse-type' className='text-xs'>
                    {t('activityDetail.browseType')}
                  </Label>
                  <Input
                    id='browse-type'
                    value={browseType}
                    onChange={(e) => setBrowseType(e.target.value)}
                    placeholder={t('activityDetail.browseTypePlaceholder')}
                    className='mt-1'
                  />
                </div>
                <div>
                  <Label className='text-xs'>{t('activityDetail.browseSort')}</Label>
                  <Select value={browseSort} onValueChange={setBrowseSort}>
                    <SelectTrigger className='mt-1'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BROWSE_SORT_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {t(opt.labelKey)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {browseTypes.length > 0 && (
                <p className='text-[11px] text-muted-foreground'>
                  {t('activityDetail.browseTypesHint')}{' '}
                  {browseTypes.map((row) => `${row.type} (${row.count})`).join(', ')}
                </p>
              )}
              <Button
                type='button'
                className='w-full sm:w-auto'
                onClick={handleBrowseActivitiesSearch}
                disabled={browseLoading}
              >
                {browseLoading ? (
                  <Loader2 className='h-4 w-4 animate-spin mr-2' />
                ) : (
                  <Search className='h-4 w-4 mr-2' />
                )}
                {t('activityDetail.browseFetch')}
              </Button>
              <div className='space-y-4 pr-1'>
                {!browseHasSearched ? (
                  <p className='text-sm text-muted-foreground py-2'>{t('activityDetail.browsePrompt')}</p>
                ) : browseLoading ? (
                  <div className='flex justify-center py-6'>
                    <Loader2 className='h-8 w-8 animate-spin text-primary' />
                  </div>
                ) : browseResults.length === 0 ? (
                  <p className='text-sm text-muted-foreground py-2'>{t('activityDetail.browseNoResults')}</p>
                ) : (
                  browseResults.map((row) => {
                    const hasMap = !!(row.polyline || row.summary_polyline);
                    return (
                      <StravaActivityListCard
                        key={row.id}
                        activity={row}
                        t={t}
                        surface='card'
                        showMap={hasMap}
                        mapHeight={140}
                        actions={(
                          <>
                            <Button
                              variant='outline'
                              size='sm'
                              className='h-8 text-xs'
                              onClick={() => {
                                navigate(`/activity/${row.id}`);
                                onOpenChange(false);
                              }}
                            >
                              <ExternalLink className='h-3 w-3 mr-1' />
                              {t('activityDetail.openActivity')}
                            </Button>
                            <Button
                              variant='secondary'
                              size='sm'
                              className='h-8 text-xs'
                              onClick={() => {
                                onOpenChange(false);
                                onCompareBrowse(row, browseResults);
                              }}
                            >
                              <Scale className='h-3 w-3 mr-1' />
                              {t('activityDetail.compare')}
                            </Button>
                          </>
                        )}
                      />
                    );
                  })
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
}
